import { useMemo } from "react";

import { FACheckboxInput } from "@impero-com/common/components/form/formAtomFields/faCheckboxInput";
import { FASelectInput } from "@impero-com/common/components/form/formAtomFields/faSelectInput";
import { formAtom } from "@impero-com/common/utils/form/formAtom";
import { useFormAtom } from "@impero-com/common/utils/form/formAtom/hooks";
import { FormAtom } from "@impero-com/common/utils/form/formAtom/types";
import { t } from "@impero-com/common/utils/i18n";
import { FormBox, FormField } from "@impero-com/ui";
import {
    atom as createAtom,
    SetStateAction,
    useAtomValue,
    WritableAtom,
} from "jotai";
import { focusAtom } from "jotai-optics";
import { isEmpty } from "lodash-es";
import * as Yup from "yup";

import { ConditionalAction, ConditionalOperator } from "api/generated/model";
import { FieldCondition, Field } from "components/fieldBuilder/models";
import { FARadioButtonGroupInput } from "components/form/formAtomFields/faRadioButtonGroupInput";
import { isNotNull } from "utils/typePredicates";

import * as theme from "./theme.css";

interface ConditionEditorProps<
    F extends Field<FT, string>,
    FT extends { type: FTN },
    FTN extends string,
> {
    atom: FormAtom<FieldCondition<string> | null>;
    fieldsBefore: Array<F>;
    conditionField: F | null;
    fieldError: string | undefined;
    getFieldTypeConditionalValues: (fieldType: FT) => Array<string>;
    disabled?: boolean;
}
export function ConditionEditor<
    F extends Field<FT, string>,
    FT extends { type: FTN },
    FTN extends string,
>({
    atom,
    fieldsBefore,
    conditionField,
    fieldError,
    getFieldTypeConditionalValues,
    disabled = false,
}: ConditionEditorProps<F, FT, FTN>) {
    const hasConditionAtom = useMemo(() => {
        return formAtom(
            focusAtom(atom, (o) =>
                o.iso(
                    (condition) => condition !== null,
                    (checked) =>
                        checked
                            ? {
                                  action: "Show",
                                  operator: "Equal",
                                  fieldId: "",
                                  value: "",
                              }
                            : null
                )
            ),
            Yup.boolean().defined(),
            atom
        );
    }, [atom]);

    const fieldCondition = useAtomValue(atom);
    const fieldId = fieldCondition?.fieldId;

    const fieldConditionAtomsAtom = useMemo(() => {
        const guard = isNotNull;
        // This atom is safe to access as long as the guard is true
        const unsafeFocusedAtom = focusAtom(atom, (o) =>
            o.guard(guard)
        ) as WritableAtom<
            FieldCondition<string>,
            [SetStateAction<FieldCondition<string>>],
            unknown
        >;

        // If there is no fieldId selected, we don't want to irritate the user with an error on
        // the value field, because there are no yet options to select from.
        let valueValidation = Yup.string().defined();
        if (fieldId) {
            valueValidation = valueValidation.required(
                t("FieldBuilder_Error_Conditional_Value_Required")
            );
        }

        const fieldConditionAtoms = {
            actionAtom: formAtom(
                focusAtom(unsafeFocusedAtom, (o) => o.prop("action")),
                Yup.string()
                    .oneOf<ConditionalAction>(["Show", "Hide"])
                    .defined(),
                atom
            ),
            operatorAtom: formAtom(
                focusAtom(unsafeFocusedAtom, (o) => o.prop("operator")),
                Yup.string()
                    .oneOf<ConditionalOperator>(["Equal", "NotEqual"])
                    .defined(),
                atom
            ),
            fieldIdAtom: formAtom(
                focusAtom(unsafeFocusedAtom, (o) => o.prop("fieldId")),
                Yup.string()
                    .defined()
                    .required(
                        t("FieldBuilder_Error_Conditional_Field_Required")
                    ),
                atom
            ),
            valueAtom: formAtom(
                focusAtom(unsafeFocusedAtom, (o) => o.prop("value")),
                valueValidation,
                atom
            ),
        };

        return createAtom((get) =>
            guard(get(atom)) ? fieldConditionAtoms : null
        );
    }, [atom, fieldId]);

    const { value, error } = useFormAtom(atom);
    const fieldConditionAtoms = useAtomValue(fieldConditionAtomsAtom);

    const operatorLabels = useMemo((): Record<ConditionalOperator, string> => {
        return {
            Equal: t("FieldBuilder_Drawer_Conditional_Operator_Equal"),
            NotEqual: t("FieldBuilder_Drawer_Conditional_Operator_NotEqual"),
        };
    }, []);
    const actionLabels = useMemo((): Record<ConditionalAction, string> => {
        return {
            Show: t("FieldBuilder_Drawer_Conditional_Action_Show"),
            Hide: t("FieldBuilder_Drawer_Conditional_Action_Hide"),
        };
    }, []);
    const allFields = useMemo(() => {
        if (!conditionField) {
            return fieldsBefore;
        }
        const conditionFieldIndex = fieldsBefore.findIndex(
            (f) => f === conditionField
        );
        if (conditionFieldIndex >= 0) {
            return fieldsBefore;
        } else {
            return fieldsBefore.concat([conditionField]);
        }
    }, [conditionField, fieldsBefore]);

    const noOptions = isEmpty(fieldsBefore);
    const hasErrors = error !== undefined || fieldError !== undefined;
    const toggleDisabled =
        disabled || (noOptions && value === null && !hasErrors);
    const fieldsDisabled = disabled || noOptions;

    return (
        <FormBox>
            <FormField error={error?.value || fieldError}>
                <div className={theme.conditionalRow}>
                    <FACheckboxInput
                        atom={hasConditionAtom}
                        label={t("FieldBuilder_Drawer_Conditional")}
                        disabled={toggleDisabled}
                    />
                </div>
                {fieldConditionAtoms && (
                    <div>
                        <div className={theme.conditionalIfRow}>
                            <span>
                                {t("FieldBuilder_Drawer_Conditional_If")}
                            </span>
                            <span className={theme.conditionalSmallSpace}>
                                &nbsp;
                            </span>
                            <div className={theme.conditionalConditionField}>
                                <FASelectInput
                                    atom={fieldConditionAtoms.fieldIdAtom}
                                    options={allFields.map((field) => ({
                                        label: field.title,
                                        value: field.id,
                                    }))}
                                    disabled={fieldsDisabled}
                                />
                            </div>
                        </div>
                        <div className={theme.conditionalComparisonRow}>
                            <FASelectInput
                                atom={fieldConditionAtoms.operatorAtom}
                                options={[
                                    "Equal" as const,
                                    "NotEqual" as const,
                                ].map((operator: ConditionalOperator) => ({
                                    label: operatorLabels[operator],
                                    value: operator,
                                }))}
                                disabled={fieldsDisabled}
                            />
                            <span className={theme.conditionalSmallSpace}>
                                &nbsp;
                            </span>
                            <div className={theme.conditionalValue}>
                                <FASelectInput
                                    atom={fieldConditionAtoms.valueAtom}
                                    options={
                                        conditionField
                                            ? getFieldTypeConditionalValues(
                                                  conditionField.fieldType
                                              ).map((value) => ({
                                                  label: value,
                                                  value,
                                              }))
                                            : []
                                    }
                                    disabled={fieldsDisabled}
                                />
                            </div>
                        </div>
                        <div className={theme.conditionalThenRow}>
                            {t("FieldBuilder_Drawer_Conditional_Then")}
                            <span className={theme.conditionalBigSpace}>
                                &nbsp;
                            </span>
                            <FARadioButtonGroupInput
                                atom={fieldConditionAtoms.actionAtom}
                                disabled={fieldsDisabled}
                                options={["Show" as const, "Hide" as const].map(
                                    (action: ConditionalAction) => ({
                                        label: actionLabels[action],
                                        value: action,
                                    })
                                )}
                                columns={2}
                            />
                        </div>
                    </div>
                )}
            </FormField>
        </FormBox>
    );
}
